import type Behavior from 'web-app/behaviors/behavior';
import {uniquenessFilter} from 'web-app/util/array';
import {extractIdsFromUrl} from 'web-app/util/famlytics/extract-ids-from-url';

export const getMixpanelGroupAnalyticsProperties = (enableAnalyticsBehavior: Behavior | undefined) => {
    const createMixpanelGroupAnalyticsProperties = (
        institutionId: string | undefined,
        organizationId: string | undefined,
    ) => {
        /**
         * The keys here, `"Institution"` and `"Organisation"` are manually defined in Mixpanel by an admin (e.g. Suvi).
         * If you change the naming of these keys, we'll also have to manually update them in Mixpanel :-)
         */
        return {
            Institution: institutionId,
            Organisation: organizationId,
        };
    };

    /** All the institutions a user has access to
     *
     * We've observed duplicates in this array, so to avoid this messing up our analytics we use the `uniquenessFilter`
     */
    const uniqueInstitutionIds = (enableAnalyticsBehavior?.payload?.meta?.institutionIds ?? []).filter(
        uniquenessFilter(instId => instId),
    );
    /** All the organizations a user has access to
     *
     * We've observed duplicates in this array, so to avoid this messing up our analytics we use the `uniquenessFilter`
     */
    const uniqueOrganizationIds = (enableAnalyticsBehavior?.payload?.meta?.organizationIds ?? []).filter(
        uniquenessFilter(orgId => orgId),
    );

    /**
     * Mixpanel only handles 1 entity per group analytics property.
     *
     * In the case where a user has access to multiple organizations AND/OR multiple institutions we'd optimally
     * want to detect for which entity (institution/organization) a user is performing an action for when we send an event.
     *
     * We now attempt to extract the institution/organization ID from the current URL as a fallback
     * when a user has multiple associations.
     */

    // Initialize with values from behavior if user has exactly one association
    const initialInstitutionId = uniqueInstitutionIds.length === 1 ? uniqueInstitutionIds[0] : undefined;
    const initialOrganizationId = uniqueOrganizationIds.length === 1 ? uniqueOrganizationIds[0] : undefined;

    // Try to extract IDs from the current URL if needed
    const extractedIds =
        (uniqueInstitutionIds.length > 1 || uniqueOrganizationIds.length > 1 || !enableAnalyticsBehavior) &&
        typeof window !== 'undefined'
            ? extractIdsFromUrl(window.location.hash)
            : {institutionId: undefined, organizationId: undefined};

    // Determine final IDs to use, prioritizing behavior data for single associations
    const finalInstitutionId =
        uniqueInstitutionIds.length !== 1 && extractedIds.institutionId
            ? extractedIds.institutionId
            : initialInstitutionId;

    const finalOrganizationId =
        uniqueOrganizationIds.length !== 1 && extractedIds.organizationId
            ? extractedIds.organizationId
            : initialOrganizationId;

    return createMixpanelGroupAnalyticsProperties(finalInstitutionId, finalOrganizationId);
};
